import React, { useEffect, useMemo, useState } from 'react';
import { FiZap } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Modal, Button, Toggle } from '../../../components/common';
import CmsField from '../../cms/components/CmsField';
import { billingClientService } from '../../../services/billingClientService';
import { getApiError, getApiFieldErrors } from '../../../services/cmsService';
import { GSTIN_REGEX, normalizeGstin } from '../billingUtils';

const emptyClient = (name = '') => ({
  name,
  clientCode: '',
  gstin: '',
  stateCode: '',
  address: '',
  email: '',
  phone: '',
  contactPerson: '',
  notes: '',
  isActive: true,
});

/**
 * Create or edit a billing client.
 *
 * `quick` trims the form to what an invoice needs (name, GSTIN, address,
 * client ID, state), for the "+ New client" row in the invoice editor.
 * `onSaved(client)` receives the normalized client returned by the API.
 */
const ClientFormModal = ({ isOpen, onClose, onSaved, client = null, initialName = '', quick = false }) => {
  const isEdit = Boolean(client?.id);
  const [form, setForm] = useState(emptyClient());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [states, setStates] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setForm(client ? { ...emptyClient(), ...client } : emptyClient(initialName));
    setErrors({});
  }, [isOpen, client, initialName]);

  useEffect(() => {
    if (!isOpen || states.length) return;
    billingClientService.states().then(setStates).catch(() => {});
  }, [isOpen, states.length]);

  const stateName = (code) => states.find((s) => s.code === code)?.name;

  // Instant format feedback; the server remains the authority on the checksum.
  const gstinHint = useMemo(() => {
    const gstin = form.gstin;
    if (!gstin) return { text: 'Optional. Without a GSTIN, pick the state below.', tone: '' };
    if (gstin.length < 15) return { text: `${gstin.length}/15 characters`, tone: '' };
    if (!GSTIN_REGEX.test(gstin)) return { text: 'This does not look like a valid GSTIN.', tone: 'bad' };
    const code = gstin.slice(0, 2);
    const name = stateName(code);
    return name
      ? { text: `${code} — ${name}`, tone: 'ok' }
      : { text: `Unknown state code ${code}`, tone: 'bad' };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.gstin, states]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const suggestCode = async () => {
    if (!form.name.trim()) {
      setErrors((prev) => ({ ...prev, name: 'Enter the client name first' }));
      return;
    }
    setSuggesting(true);
    try {
      setField('clientCode', await billingClientService.suggestCode(form.name.trim()));
    } catch (error) {
      toast.error(getApiError(error, 'Could not suggest a client ID'));
    } finally {
      setSuggesting(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setErrors({ name: 'Client name is required' });
      return;
    }

    setSaving(true);
    try {
      // A GSTIN decides the state server-side, so don't send a stale one.
      const payload = { ...form, name: form.name.trim(), stateCode: form.gstin ? '' : form.stateCode };
      const saved = isEdit
        ? await billingClientService.update(client.id, payload)
        : await billingClientService.create(payload);
      toast.success(isEdit ? 'Client updated' : `Client ${saved.clientCode} created`);
      onSaved?.(saved);
      onClose();
    } catch (error) {
      const api = getApiFieldErrors(error);
      setErrors({
        name: api.name,
        clientCode: api.client_code,
        gstin: api.gstin,
        stateCode: api.state_code,
        address: api.address,
        email: api.email,
        phone: api.phone,
        contactPerson: api.contact_person,
        notes: api.notes,
      });
      toast.error(getApiError(error, 'Failed to save the client'));
    } finally {
      setSaving(false);
    }
  };

  const stateOptions = states.map((s) => ({ value: s.code, label: `${s.code} — ${s.name}` }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Billing Client' : 'New Billing Client'}
      size="large"
      closeOnOverlay={false}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} loading={saving}>{isEdit ? 'Save changes' : 'Create client'}</Button>
        </>
      }
    >
      <div className="bl-form-stack">
        <div className="bl-form-grid">
          <div className="bl-span-all">
            <CmsField
              id="bc-name"
              label="Client name"
              required
              placeholder="e.g. M/S BEARWORX ENGINEERING PVT LTD"
              value={form.name}
              onChange={(v) => setField('name', v)}
              error={errors.name}
            />
          </div>
          <div>
            <CmsField
              id="bc-gstin"
              label="GSTIN"
              placeholder="36AAGCB8551L3Z7"
              value={form.gstin}
              onChange={(v) => setField('gstin', normalizeGstin(v).slice(0, 15))}
              error={errors.gstin}
              inputProps={{ maxLength: 15, spellCheck: false, autoCapitalize: 'characters' }}
            />
            {!errors.gstin && (
              <span className={`cf-hint ${gstinHint.tone ? `bl-hint-${gstinHint.tone}` : ''}`}>{gstinHint.text}</span>
            )}
          </div>
          <CmsField
            id="bc-state"
            type="select"
            label="State"
            placeholder={form.gstin ? 'Taken from the GSTIN' : 'Select state'}
            options={stateOptions}
            value={form.gstin ? '' : form.stateCode}
            onChange={(v) => setField('stateCode', v)}
            disabled={Boolean(form.gstin)}
            hint={form.gstin ? undefined : 'Needed to decide IGST vs CGST + SGST.'}
            error={errors.stateCode}
          />
          <div className="bl-inline-field">
            <CmsField
              id="bc-code"
              label="Client ID"
              placeholder="Generated if left blank"
              value={form.clientCode}
              onChange={(v) => setField('clientCode', v)}
              error={errors.clientCode}
            />
            <Button variant="outline" icon={<FiZap />} onClick={suggestCode} loading={suggesting} title="Suggest a client ID from the name">
              Suggest
            </Button>
          </div>
          <div className="bl-span-all">
            <CmsField
              id="bc-address"
              type="textarea"
              label="Address"
              rows={3}
              value={form.address}
              onChange={(v) => setField('address', v)}
              error={errors.address}
            />
          </div>

          {!quick && (
            <>
              <CmsField id="bc-contact" label="Contact person" value={form.contactPerson} onChange={(v) => setField('contactPerson', v)} error={errors.contactPerson} />
              <CmsField id="bc-email" type="email" label="Email" value={form.email} onChange={(v) => setField('email', v)} error={errors.email} />
              <CmsField id="bc-phone" label="Phone" value={form.phone} onChange={(v) => setField('phone', v)} error={errors.phone} />
              <div className="bl-span-all">
                <CmsField
                  id="bc-notes"
                  type="textarea"
                  label="Internal notes"
                  rows={2}
                  value={form.notes}
                  onChange={(v) => setField('notes', v)}
                  error={errors.notes}
                />
              </div>
            </>
          )}
        </div>

        {!quick && (
          <Toggle
            name="bc-active"
            checked={form.isActive}
            onChange={(e) => setField('isActive', e.target.checked)}
            label={form.isActive ? 'Active — appears in invoice search' : 'Inactive — hidden from invoice search'}
          />
        )}
      </div>
    </Modal>
  );
};

export default ClientFormModal;
